import type { UserPreferencesNotificationsHeaderProps } from "./user-preferences-notifications-header.type";
import type {
  UserPreferencesNotificationsMenuItem,
  UserPreferencesNotificationsMenuItemGroup,
  UserPreferencesNotificationsMenuItemOptions,
} from "../user-preferences-notifications-dropdown";
import type { PartialSettingsNotifications } from "../user-preferences-notifications.type";
import type { NotificationsChannelType } from "@/entities/referential/referential-notifications";

import { useTranslation } from "react-i18next";

import { useUserPreferencesNotificationsDropdownBuilder } from "../user-preferences-notifications-dropdown";
import { useUserPreferencesNotificationsContext } from "../user-preferences-notifications.context";
import { getSettingChannelFromAction } from "../user-preferences-notifications.helper";

export const useUserPreferencesNotificationsHeader = ({
  notifications,
}: UserPreferencesNotificationsHeaderProps) => {
  const { menuItems } = useUserPreferencesNotificationsHeaderMenuItems();
  const { defaultSettings, onSettingsChange } =
    useUserPreferencesNotificationsContext();

  const onReloadClick = () => {
    if (!defaultSettings) return;
    onSettingsChange(defaultSettings);
  };

  const onMenuItemClick = ({
    action,
    channel,
  }: UserPreferencesNotificationsMenuItemOptions) => {
    if (!channel) return;

    const newSettings: PartialSettingsNotifications = {};

    notifications.forEach((notification) => {
      const defaultValue = defaultSettings?.[notification.slug]?.[channel];
      const newValue = getSettingChannelFromAction(action, defaultValue);
      newSettings[notification.slug] = { [channel]: newValue };
    });

    onSettingsChange(newSettings);
  };

  return {
    menuItems,
    onReloadClick,
    onMenuItemClick,
  };
};

export const useUserPreferencesNotificationsHeaderMenuItems = () => {
  const { t } = useTranslation();

  const { buildAction, buildDivider } =
    useUserPreferencesNotificationsDropdownBuilder();

  const buildGroup = (
    channel: NotificationsChannelType
  ): UserPreferencesNotificationsMenuItemGroup => {
    return {
      key: channel,
      type: "group",
      label: t(`user:preferences.notifications.channels.${channel}`),
      children: [
        buildAction("activate", channel),
        buildAction("disable", channel),
      ],
    };
  };

  const menuItems: UserPreferencesNotificationsMenuItem[] = [
    buildGroup("database"),
    buildDivider(),
    buildGroup("broadcast"),
    buildDivider(),
    buildGroup("mail"),
  ];

  return {
    menuItems,
  };
};
